import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import {
  Search, 
  Bell, 
  User, 
  Moon, 
  Sun, 
  Maximize2,
  Menu,
  ChevronDown,
  UserCircle,
  Shield,
  LogOut,
  Sliders
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { useTheme } from '../../App';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { useNavigate } from 'react-router-dom';
import { isTeamChatEnabled } from '../../modules/team-chat/featureFlag';
import { TeamChatHeaderTrigger } from '../../modules/team-chat/components/TeamChatHeaderTrigger';

/** Sample notifications until a notifications API is connected (badge count should stay in sync). */
const HEADER_NOTIFICATIONS: Array<{
  id: string;
  title: string;
  description: string;
  time: string;
  href: string;
}> = [
  {
    id: '1',
    title: 'High CPU usage',
    description: 'api-server-01 exceeded 85% for 5 minutes',
    time: '2m ago',
    href: '/monitoring/alerts',
  },
  {
    id: '2',
    title: 'Deployment finished',
    description: 'Production release v2.4.1 completed successfully',
    time: '1h ago',
    href: '/monitoring/alerts',
  },
  {
    id: '3',
    title: 'SSL certificate expiring',
    description: 'edge-gateway cert expires in 14 days',
    time: '3h ago',
    href: '/monitoring/alerts',
  },
];

interface DashboardHeaderProps {
  currentPage: string;
  onToggleSidebar: () => void;
  onToggleMobileMenu?: () => void;
  onOpenSearch?: () => void;
}

export function DashboardHeader({ currentPage, onToggleSidebar, onToggleMobileMenu, onOpenSearch }: DashboardHeaderProps) {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const responsive = useResponsive();
  const navigate = useNavigate();
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('up');
  const lastScrollTopRef = useRef(0);
  const scrollStopTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const scrollElement = document.querySelector('[data-dashboard-scroll="true"]') as HTMLElement | null;
    if (!scrollElement) return;

    const onScroll = () => {
      const nextTop = scrollElement.scrollTop;
      const delta = nextTop - lastScrollTopRef.current;
      if (Math.abs(delta) < 2) return;

      setScrollDirection(delta > 0 ? 'down' : 'up');
      setIsUserScrolling(true);
      lastScrollTopRef.current = nextTop;

      if (scrollStopTimeoutRef.current !== null) {
        window.clearTimeout(scrollStopTimeoutRef.current);
      }
      scrollStopTimeoutRef.current = window.setTimeout(() => {
        setIsUserScrolling(false);
      }, 140);
    };

    scrollElement.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scrollElement.removeEventListener('scroll', onScroll);
      if (scrollStopTimeoutRef.current !== null) {
        window.clearTimeout(scrollStopTimeoutRef.current);
      }
    };
  }, []);

  void currentPage;
  void onToggleSidebar;

  return (
    <header
      className={`sticky top-0 z-30 border-b border-border bg-background/95 dark:bg-gradient-to-r dark:from-dashboard-bg dark:via-dashboard-bg/95 dark:to-dashboard-bg backdrop-blur-xl relative transition-transform duration-200 ${
        isUserScrolling && scrollDirection === 'down' ? '-translate-y-1' : 'translate-y-0'
      }`}
    >
      {/* Ambient glow */}
      <div className="absolute inset-0 bg-gradient-to-r from-glow-cyan/5 via-transparent to-glow-blue/5 dark:from-glow-cyan/5 dark:via-transparent dark:to-glow-blue/5" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyber-cyan/20 dark:via-cyber-cyan/40 to-transparent" />
      
      <div className="h-16 md:h-20 flex items-center justify-between px-3 sm:px-4 md:px-6 lg:px-8 relative z-10 gap-2 md:gap-4">
        <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
          {(responsive.isMobile || responsive.isTablet) && onToggleMobileMenu ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleMobileMenu}
              className="p-2 hover:bg-card-bg/50 text-slate-400 hover:text-cyber-cyan transition-all duration-300 border border-transparent hover:border-card-border/50 rounded-lg backdrop-blur-sm"
            >
              <Menu className="w-5 h-5" />
            </Button>
          ) : null}

          {responsive.isMdUp && (
          <div className="w-full max-w-2xl">
            <div
              onClick={() => onOpenSearch && onOpenSearch()}
              className="relative group cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyber-cyan/20 to-cyber-blue/20 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 group-hover:text-cyber-cyan transition-colors duration-300" />
                <div className="pl-12 pr-24 py-3 bg-card-bg/50 border border-card-border/50 hover:border-cyber-cyan/60 hover:bg-card-bg/80 rounded-xl text-slate-500 backdrop-blur-xl transition-all duration-300">
                  Search projects, pages, features...
                </div>
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
                  <kbd className="px-2 py-1 text-xs bg-card-bg border border-card-border/50 rounded-md text-slate-400 font-mono">
                    ⌘K
                  </kbd>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-1 sm:gap-2 md:gap-3 flex-shrink-0">
          {!responsive.isMdUp && (
            <motion.button
              onClick={() => onOpenSearch && onOpenSearch()}
              className="p-2 md:p-3 rounded-lg bg-card-bg/50 hover:bg-card-bg/80 border border-card-border/30 hover:border-card-border/60 transition-all duration-300 group backdrop-blur-sm"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <Search className="w-4 h-4 text-slate-400 group-hover:text-cyber-cyan transition-colors" />
            </motion.button>
          )}

          {/* Control Buttons */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Theme Toggle */}
            <motion.button
              onClick={toggleTheme}
              className="p-2 md:p-3 rounded-lg bg-card-bg/50 hover:bg-card-bg/80 border border-card-border/30 hover:border-card-border/60 transition-all duration-300 group backdrop-blur-sm"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              {theme === 'dark' ? (
                <Sun className="w-4 h-4 text-yellow-400 group-hover:text-yellow-300 transition-colors" />
              ) : (
                <Moon className="w-4 h-4 text-slate-400 group-hover:text-slate-300 transition-colors" />
              )}
            </motion.button>

            {/* Fullscreen Toggle - Only on medium screens and up */}
            {responsive.isMdUp && (
              <motion.button
                onClick={() => {
                  if (document.fullscreenElement) {
                    document.exitFullscreen();
                  } else {
                    document.documentElement.requestFullscreen();
                  }
                }}
                className="p-2 md:p-3 rounded-lg bg-card-bg/50 hover:bg-card-bg/80 border border-card-border/30 hover:border-card-border/60 transition-all duration-300 group backdrop-blur-sm"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Maximize2 className="w-4 h-4 text-slate-400 group-hover:text-cyber-cyan transition-colors" />
              </motion.button>
            )}

            {isTeamChatEnabled() ? <TeamChatHeaderTrigger /> : null}

            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <motion.button
                  type="button"
                  aria-label="Open notifications"
                  className="relative p-2 md:p-3 rounded-lg bg-card-bg/50 hover:bg-card-bg/80 border border-card-border/30 hover:border-card-border/60 transition-all duration-300 group backdrop-blur-sm"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Bell className="w-4 h-4 text-slate-400 group-hover:text-cyber-cyan transition-colors" />
                  <Badge
                    variant="destructive"
                    className="absolute -top-1 -right-1 w-4 h-4 md:w-5 md:h-5 p-0 flex items-center justify-center text-xs bg-gradient-to-r from-red-500 to-red-600 text-white border-0 shadow-lg shadow-red-500/30"
                  >
                    {HEADER_NOTIFICATIONS.length}
                  </Badge>
                </motion.button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 sm:w-96 max-h-[min(70vh,28rem)] overflow-y-auto">
                <DropdownMenuLabel className="font-semibold text-white">Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {HEADER_NOTIFICATIONS.map((n) => (
                  <DropdownMenuItem
                    key={n.id}
                    className="flex cursor-pointer flex-col items-start gap-0.5 py-3"
                    onClick={() => navigate(n.href)}
                  >
                    <span className="text-sm font-medium text-white">{n.title}</span>
                    <span className="text-xs text-slate-400 leading-snug">{n.description}</span>
                    <span className="text-[11px] text-slate-500">{n.time}</span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="cursor-pointer text-cyber-cyan focus:text-cyber-cyan"
                  onClick={() => navigate('/monitoring/alerts')}
                >
                  View all alerts &amp; notifications
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <motion.button
                className="flex items-center space-x-2 md:space-x-3 px-2 md:px-4 py-2 md:py-3 rounded-xl bg-gradient-to-r from-card-bg to-card-bg/50 hover:from-card-bg/80 hover:to-card-bg/60 border border-card-border/30 hover:border-card-border/60 transition-all duration-300 backdrop-blur-sm shadow-lg"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-cyber-cyan via-cyber-blue to-cyber-purple rounded-xl flex items-center justify-center shadow-lg shadow-cyber-cyan/25">
                  <User className="w-4 h-4 md:w-5 md:h-5 text-white" />
                </div>
                {responsive.isSmUp && (
                  <div className="text-left">
                    <p className="text-sm font-semibold text-white truncate max-w-[120px]">
                      {user?.name || 'Admin User'}
                    </p>
                    {responsive.isMdUp && (
                      <p className="text-xs text-slate-400">
                        Administrator
                      </p>
                    )}
                  </div>
                )}
                {responsive.isSmUp && <ChevronDown className="w-4 h-4 text-slate-400" />}
              </motion.button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="truncate">
                {user?.email || 'admin@coremonit.com'}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/team/members')}>
                <UserCircle className="w-4 h-4" />
                My Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/system/settings')}>
                <Sliders className="w-4 h-4" />
                Account Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/system/security-events')}>
                <Shield className="w-4 h-4" />
                Security
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onClick={logout}>
                <LogOut className="w-4 h-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}